import React from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector, useDispatch } from 'react-redux';

import Button from 'components/Button';
import FormInput from 'components/FormInput';
import PhoneInput from 'components/PhoneInput';
import { updateUser } from 'redux/slices/auth';
import { profile as TEXT } from 'locales/en';
import schema, { FIELD_NAMES } from './schema';
import { logEvent } from 'utils/ga';

const Profile = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const defaultValues = {
    first_name: user.firstName,
    last_name: user.lastName,
    email: user.email,
    phone: user.phone || '',
    phone_country: { ...user.phone_country },
    password: undefined,
  };
  const { register, handleSubmit, control } = useForm({
    resolver: yupResolver(schema),
  });
  const { errors, isSubmitting } = useFormState({ control });

  const onSubmit = (data) => {
    if (!data.password) delete data.password;
    const { phone_country, phone, ...rest } = data;

    return dispatch(
      updateUser({
        ...rest,
        phone_number: `${phone_country.dial_code}${phone}`,
      })
    ).then(() =>
      logEvent('click', { category: 'update', label: 'profile_update' })
    );
  };

  if (!user.name) return null;

  return (
    <div>
      <h1 className="text-javelin-gray-dark font-bold text-xl lg:mt-12 mb-7">
        {user.name}
      </h1>
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="w-full flex flex-col items-end"
      >
        <div className="card lg:pl-12 py-12 w-full rounded-md mb-8 shadow-sm overflow-visible">
          <div className="flex flex-col justify-between">
            <FormInput
              label={TEXT.first_name}
              name={FIELD_NAMES.first_name}
              register={register}
              inputClassName="xl:w-144 lg:py-2"
              labelClassName="lg:w-3/12"
              defaultValue={defaultValues.first_name}
            />
            <FormInput
              label={TEXT.last_name}
              name={FIELD_NAMES.last_name}
              register={register}
              inputClassName="xl:w-144 lg:py-2"
              labelClassName="lg:w-3/12"
              defaultValue={defaultValues.last_name}
            />
            <FormInput
              label={TEXT.email}
              name={FIELD_NAMES.email}
              type="email"
              register={register}
              inputClassName="xl:w-144 lg:py-2"
              labelClassName="lg:w-3/12"
              error={errors?.[FIELD_NAMES.email]?.message}
              defaultValue={defaultValues.email}
            />
            <PhoneInput
              name={FIELD_NAMES.phone}
              control={control}
              countryFieldName={FIELD_NAMES.phone_country}
              label={TEXT.phone}
              error={errors.phone?.message}
              defaultCountry={defaultValues.phone_country}
              defaultPhoneNumber={defaultValues.phone}
              className="xl:w-144 lg:py-2 input-form-wrapper"
              labelClassName="lg:w-3/12"
              inputBelowLabel={false}
            />
            <FormInput
              label={TEXT.password}
              name={FIELD_NAMES.password}
              type="password"
              register={register}
              inputClassName="xl:w-144 lg:py-2"
              labelClassName="lg:w-3/12"
              error={errors?.[FIELD_NAMES.password]?.message}
            />
          </div>
        </div>
        <Button
          className="btn-primary py-3 px-2 w-56"
          title={TEXT.update}
          type="submit"
          showSpinner={isSubmitting}
        />
      </form>
    </div>
  );
};

export default Profile;
